import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';

import {
  DataTable,
  DataTableColumn,
  DataTableCell,
  Dropdown,
  Tooltip
} from '@salesforce/design-system-react';

import './ExperimentList.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const { formatNumber } = props.intl;
  switch (property) {
    case 'actions':
      const isSaveAllowed = !props.flags?.isReadonly;
      const has_selectable = props.item?.assets > 0;
      // The tooltip needs undefined for isOpen value, in order to open on hover
      const open = has_selectable ? undefined : false;
      const disabled = !has_selectable || !isSaveAllowed;
      const actions = [];
      if (props.item.status !== 'FINISHED') {
        actions.push({
          disabled: !isSaveAllowed,
          label: props.formatMessage({
            id: 'components.experiment.menu.edit'
          }),
          value: 'edit'
        });
      }

      if (props.item.status === 'DRAFT') {
        actions.push({
          disabled: disabled,
          label: props.formatMessage({
            id: 'components.experiment.menu.publish'
          }),
          value: 'publish'
        });
        actions.push({
          disabled: !isSaveAllowed,
          label: props.formatMessage({
            id: 'components.experiment.menu.delete'
          }),
          value: 'delete'
        });
      }

      if (props.item.status === 'FINISHED') {
        actions.push({
          disabled: !isSaveAllowed,
          label: props.formatMessage({
            id: props.item.archived
              ? 'components.experiment.menu.unarchive'
              : 'components.experiment.menu.archive'
          }),
          value: 'archive'
        });
      }

      if (props.item.status === 'PUBLISHED') {
        actions.push({
          disabled: !isSaveAllowed,
          label: props.formatMessage({
            id: 'components.experiment.menu.end'
          }),
          value: 'end'
        });
      }

      const dropDown = (
        <Dropdown
          assistiveText={{ icon: 'Content Test Options' }}
          buttonVariant="icon"
          iconCategory="utility"
          iconName="down"
          iconVariant="border-filled"
          align="right"
          className="actions-dropdown"
          width="xx-small"
          options={actions}
          onSelect={({ value }) => props.onAction(value, props.item, disabled)}
          tooltipMenuItem={
            <Tooltip
              id="drop-down-tooltip"
              align="left"
              position="overflowBoundaryElement"
              isOpen={open}
            />
          }
        />
      );
      return <td className={'experiment-cell actions-cell'}>{dropDown}</td>;
    case 'ctor':
      const percentTotal =
        props.item.status === 'DRAFT'
          ? null
          : formatNumber(props.item.clicks / props.item.selections || 0, {
              style: 'percent',
              maximumFractionDigits: 2
            });
      return <td className="experiment-cell truncate">{percentTotal}</td>;
    case 'status':
      return (
        <td className={'experiment-cell truncate'}>
          {props.formatMessage({
            id: `components.experiment.testStatus.${children}`
          })}
        </td>
      );
    case 'testMode':
      return (
        <td className={'experiment-cell truncate'}>
          {props.formatMessage({
            id: `components.experiment.engineName.${children}`
          })}
        </td>
      );
    case 'name':
      const analytics_url = `/experiments/analytics/${props.item.uuid}`;
      return (
        <td className={'experiment-cell truncate'}>
          <a href={analytics_url}>{children}</a>
        </td>
      );
    case 'selections':
    case 'clicks':
      return (
        <td className={'experiment-cell truncate'}>
          {props.item.status === 'DRAFT' ? null : children}
        </td>
      );
    default: {
      return <td className={'experiment-cell truncate'}>{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class ExperimentList extends Component {
  headerCell(property, formatMessage) {
    return formatMessage({ id: `components.experiment.${property}` });
  }

  columns(flags) {
    const isReadonly = flags?.isReadonly;
    const cols = {
      name: { sortable: false },
      description: { sortable: false },
      status: { sortable: false, width: '100px' },
      testMode: { sortable: false, width: '175px' },
      assets: { sortable: false, width: '75px' },
      selections: { sortable: false, width: '90px' },
      clicks: { sortable: false, width: '95px' },
      ctor: { sortable: false, width: '95px' },
      createdBy: { sortable: false },
      updatedAt: { sortable: false }
    };
    if (flags && !isReadonly) {
      cols.actions = { sortable: false, width: '50px' };
    }
    return cols;
  }

  tableData(items) {
    return items.map(item => ({
      ...item,
      id: item.uuid,
      name: item.name,
      description: item.description,
      status: item.status,
      testMode: item.engine_name,
      assets: item.content_test_assets.length || 0,
      selections: item.selections,
      clicks: item.clicks,
      createdBy: item.created_by,
      updatedAt: formatTimestamp(this.props.intl)(item.updated_at)
    }));
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;
    const { flags } = this.props;
    const columns = this.columns(flags);

    return (
      <DataTable
        items={items}
        className="experiment-list-table"
        fixedLayout
        fixedHeader={this.props.unfilteredExperiments?.length ? true : false}
        noRowHover
        hasMore={this.props.hasMore}
        onLoadMore={this.props.onLoadMore}
      >
        {Object.entries(columns).map((column, index) => {
          const sortable = column[1].sortable;
          const width = column[1].width;
          const property = column[0];
          return (
            <DataTableColumn key={index} sortable={sortable}>
              <CustomDataTableCell
                property={property}
                formatMessage={formatMessage}
                label={this.headerCell(property, formatMessage)}
                width={width}
                onAction={this.props.onListAction}
                flags={flags}
                intl={this.props.intl}
              />
            </DataTableColumn>
          );
        })}
      </DataTable>
    );
  }

  render() {
    const { experiments } = this.props;
    return this.renderTable(this.tableData(experiments));
  }
}

ExperimentList.propTypes = {
  experiments: PropTypes.array.isRequired,
  flags: PropTypes.object.isRequired,
  onListAction: PropTypes.func,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ExperimentList);
